import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment'
import React, { Fragment, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectSpendMetricsLoadingFromState } from 'src/app/AccountOverview/redux/accountOverview.selectors'
import { selectUsersFullPaginatedDataFromState } from 'src/app/AccountVirtualCards/redux/accountVirtualCards.selectors'
import { amexIssuerCodes } from 'src/app/RegisterAccount/RegisterAccount'
import AccountMetricsSummaryBox from 'src/components/AccountMetricsSummaryBox/AccountMetricsSummaryBox'
import AccountStatusBox from 'src/components/AccountStatusBox/AccountStatusBox'
import AccountVerificationBlockedBox from 'src/components/AccountVerificationBlockedBox/AccountVerificationBlockedBox'
import AccountVerificationRequiredBox from 'src/components/AccountVerificationRequiredBox/AccountVerificationRequiredBox'
import AccountVerificationTimedOutBox from 'src/components/AccountVerificationTimedOutBox/AccountVerificationTimedOutBox'
import ActiveUntilBox from 'src/components/ActiveUntilBox/ActiveUntilBox'
import AmexAccountVerificationActionRequiredBox from 'src/components/AmexAccountVerificationActionRequiredBox/AmexAccountVerificationActionRequiredBox'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import BudgetsBox from 'src/components/BudgetsBox/BudgetsBox'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import NoAccountActivity from 'src/components/NoAccountActivity/NoAccountActivity'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import PermanentPromotionBox from 'src/components/PermanentPromotionBox/PermanentPromotionBox'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBox from 'src/components/VirtualCardsBox/VirtualCardsBox'
import { ImageNames, Issuers } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import {
  CreditCardListModel,
  CreditCardStatus,
} from 'src/models/creditCard.models'
import { CreditCardPermissionsModel } from 'src/models/creditCardPermissions.models'
import {
  SpendMetricsAggregates,
  SpendMetricsPeriods,
} from 'src/models/spendMetrics.models'
import { TransactionStatus } from 'src/models/transaction.models'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import {
  createBudgetLocation,
  createVirtualCardLocation,
} from 'src/navigation/locations'
import {
  accountBudgetsUrl,
  accountOverviewUrl,
  accountVirtualCardsUrl,
} from 'src/navigation/urls'
import { createSetIsSetupOrganizationPopupOpen } from 'src/redux/actions/app.actions'
import { postSpendMetrics } from 'src/redux/actions/data/transactions.actions'
import { selectIsUserOrglessRCNOrBudgetOwner } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface AccountOverviewBackgroundPageContentProps {
  postingAccountVerification?: boolean
  accountVerificationError?: string
  postingResendAccountVerification?: boolean
  accountResendVerificationError?: string
  creditCard: CreditCardListModel
  activeVcnCount?: number
  vcnCount?: number
  activeBudgetCount?: number
  budgetCount?: number
  creditCardPermissions?: CreditCardPermissionsModel
  activeCreditCardBudgets: CreditCardListModel[]
  activeCreditCardVirtualCards: VirtualCardListModel[]
  graphData: Array<{ x: number; y: number }>
  totalSpent?: number
  virtualCardsLoading: boolean
  virtualCardsComplete: boolean
  creditCardsLoading: boolean
  creditCardsComplete: boolean
  handleVerifyCreditCard: (code: string) => void
  handleResendVerification: () => void
}

const AccountOverviewBackgroundPageContent: React.FC<
  AccountOverviewBackgroundPageContentProps
> = (props): React.ReactElement => {
  const dispatch = useBackgroundDispatch()
  const spendMetricsLoading = useSelector(selectSpendMetricsLoadingFromState)
  const isUserOrglessRCNOrBudgetOwner = useSelector(
    selectIsUserOrglessRCNOrBudgetOwner,
  )
  const usersFullPaginatedData = useSelector(
    selectUsersFullPaginatedDataFromState,
  )
  const accountClosed = props.creditCard.isClosed
  const noVirtualCards = useColorOrGrayscaleImage(ImageNames.NoVirtualCards)
  const noBudgets = useColorOrGrayscaleImage(ImageNames.NoBudgets)

  const canIssueVirtualCards = props.creditCardPermissions?.canIssueVirtualCards
  const canUpdateCreditCard = props.creditCardPermissions?.canUpdateCreditCard

  const isUserWithNoObjects =
    canIssueVirtualCards && props?.budgetCount === 0 && props?.vcnCount === 0

  const noVcnBudget = props?.budgetCount === 0 && props?.vcnCount === 0

  useEffect((): void => {
    dispatch(
      postSpendMetrics({
        aggregate: SpendMetricsAggregates.Sum,
        period: SpendMetricsPeriods.Day,
        since: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        until: moment().format('YYYY-MM-DD'),
        creditCardId: [props.creditCard.id],
        includeBudgets: true,
        status: [TransactionStatus.Cleared, TransactionStatus.Pending],
      }),
    )
  }, [props.creditCard.id])

  const handleCreateBudgetClick = (): void => {
    if (isUserOrglessRCNOrBudgetOwner) {
      dispatch(
        createSetIsSetupOrganizationPopupOpen(
          true,
          createBudgetLocation({ creditCardId: props.creditCard.id }),
        ),
      )
    }
  }

  const showBody =
    (props.creditCard.isActive || props.creditCard.isClosed) &&
    !props.virtualCardsLoading &&
    props.virtualCardsComplete

  const showPromos = isUserWithNoObjects && !accountClosed

  const showNoActivity = !showPromos && noVcnBudget && !accountClosed

  const showStatusAndMetrics = !noVcnBudget || accountClosed

  const showActiveUntil = accountClosed

  const showBudgetsBox =
    props.activeBudgetCount !== undefined && props.activeBudgetCount > 0

  const showVirtualCardsBox =
    props.activeVcnCount !== undefined && props.activeVcnCount > 0

  const isAmex = amexIssuerCodes.includes(props.creditCard.issuer as Issuers)
  const showAmexActionRequired =
    isAmex &&
    props.creditCard.isPending &&
    !props.creditCard.features.needsCardholderVerification

  const showBlocked = props.creditCard.isBlocked

  const showTimedOut =
    props.creditCard.status === CreditCardStatus.Inactive && isAmex

  const showVerificationRequired =
    props.creditCard.isPending &&
    props.creditCard.features.needsCardholderVerification

  const noActivitySubtitleSource = props.creditCard.features.budgets
    ? '${this} is where you would see a high-level snapshot of account use, virtual card spend, and the virtual cards or budgets belonging to this account.'
    : '${this} is where you would see a high-level snapshot of account use, virtual card spend, and the virtual cards belonging to this account.'

  return (
    <BackgroundPageContent noStackingIndex={props.creditCard.isPending}>
      {showBody && (
        <Fragment>
          {showPromos ? (
            <HorizontalContentCollection>
              <Fragment>
                <PermanentPromotionBox
                  imageSrc={noVirtualCards}
                  title={<Trns source={'Get started with your Account'} />}
                  subtitle={
                    <Trns
                      source={'Create a Virtual Card for someone on your team.'}
                    />
                  }
                  navAway={{
                    label: <Trns source={'Create a Virtual Card'} />,
                    to: createVirtualCardLocation({
                      creditCardId: props.creditCard.id,
                    }),
                    ariaLabelSource: 'Create a Virtual Card',
                  }}
                  usePlus
                />
                {props.creditCard.features.budgets && (
                  <PermanentPromotionBox
                    imageSrc={noBudgets}
                    title={<Trns source={'Delegate with Budgets'} />}
                    subtitle={
                      <Trns
                        source={
                          'Enable others to create Virtual Cards with Budgets.'
                        }
                      />
                    }
                    onClick={handleCreateBudgetClick}
                    navAway={{
                      label: <Trns source={'Create a Budget'} />,
                      to: !isUserOrglessRCNOrBudgetOwner
                        ? createBudgetLocation({
                            creditCardId: props.creditCard.id,
                          })
                        : accountOverviewUrl(props.creditCard.id),
                      ariaLabelSource: 'Create a Budget',
                    }}
                    usePlus
                  />
                )}
              </Fragment>
            </HorizontalContentCollection>
          ) : (
            showNoActivity && (
              <NoAccountActivity
                titleText={<Trns source={'No activity on this account'} />}
                subtitleText={
                  <Trns
                    source={noActivitySubtitleSource}
                    expressions={{
                      this: (
                        <span
                          css={css`
                            ${getFontCss(
                              16,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.medium,
                            )};
                          `}
                        >
                          <Trns source={'This'} />
                        </span>
                      ),
                    }}
                  />
                }
                budgetAccount={!!props.creditCard.features.budgets}
              />
            )
          )}

          {showStatusAndMetrics && (
            <HorizontalContentCollection>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 10px;
                `}
              >
                <AccountStatusBox creditCard={props.creditCard} />
                {showActiveUntil && (
                  <ActiveUntilBox
                    inactiveSinceString={
                      props.creditCard.inactiveSinceTimezonedString
                    }
                    activeUntilString={
                      props.creditCard.activeUntilTimezonedString
                    }
                    openLeftMdUp={false}
                    openLeftSmDown
                    isAccountClosed={accountClosed}
                  />
                )}
              </div>
              <AccountMetricsSummaryBox
                creditCard={props.creditCard}
                graphData={props.graphData}
                totalSpent={props.totalSpent}
                isLoading={spendMetricsLoading}
              />
            </HorizontalContentCollection>
          )}

          {showBudgetsBox && (
            <BudgetsBox
              budgets={props.activeCreditCardBudgets}
              isSlim={false}
              isSnapshot
              defaultTitle={
                <ObjectBoxTitle
                  title={<Trns source={'Budgets Created'} />}
                  proportional={{
                    viewable: props.activeCreditCardBudgets.length,
                    total: props.activeBudgetCount || 0,
                  }}
                />
              }
              navAway={{
                label: <Trns source={'All Budgets on this Account'} />,
                to: accountBudgetsUrl(props.creditCard.id),
                ariaLabelSource: 'All Budgets on this Account',
              }}
            />
          )}

          {showVirtualCardsBox && (
            <VirtualCardsBox
              virtualCards={props.activeCreditCardVirtualCards}
              defaultTitle={
                <ObjectBoxTitle
                  title={<Trns source={'Active Cards'} />}
                  proportional={{
                    viewable: props.activeCreditCardVirtualCards.length,
                    total: props.activeVcnCount || 0,
                  }}
                />
              }
              snapshotProps={{
                navAway: {
                  label: <Trns source={'All Cards on this Account'} />,
                  to: accountVirtualCardsUrl(props.creditCard.id),
                  ariaLabelSource: 'All Cards on this Account',
                },
              }}
              emptyStateDesign={
                TableEmptyStateDesigns.AccountVirtualCardsActive
              }
              loading={props.virtualCardsLoading}
              complete={props.virtualCardsComplete}
              totalCount={props.activeVcnCount || 0}
              isSnapshot
              usersFullPaginatedData={usersFullPaginatedData}
            />
          )}
        </Fragment>
      )}

      {showAmexActionRequired && (
        <AmexAccountVerificationActionRequiredBox
          creditCard={props.creditCard}
        />
      )}

      {showBlocked && <AccountVerificationBlockedBox />}

      {showTimedOut && <AccountVerificationTimedOutBox />}

      {showVerificationRequired && (
        <AccountVerificationRequiredBox
          accountResendVerificationError={props.accountResendVerificationError}
          accountVerificationError={props.accountVerificationError}
          canVerifyAgain={props.creditCard.canVerifyAgain}
          handleResendVerification={props.handleResendVerification}
          handleVerifyCreditCard={props.handleVerifyCreditCard}
          postingAccountVerification={props.postingAccountVerification}
          postingResendAccountVerification={
            props.postingResendAccountVerification
          }
          timeUntilNextVerificationRequested={
            props.creditCard.timeUntilNextVerificationRequested
          }
          canUpdateCreditCard={canUpdateCreditCard}
        />
      )}
    </BackgroundPageContent>
  )
}

export default AccountOverviewBackgroundPageContent
